import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const features = [
  {
    title: 'Multi-Database Search',
    description: 'Search PubMed, arXiv, OpenAlex, Europe PMC, and bioRxiv simultaneously from a single query.',
    step: '01',
    color: '#0a72ef',
  },
  {
    title: 'AI Protocol Generation',
    description: 'Generate a structured 6-section research protocol from your paper collection in seconds.',
    step: '02',
    color: '#de1d8d',
  },
  {
    title: 'Hypothesis Generator',
    description: 'AI produces testable hypotheses with rationale and experimental approach based on your literature.',
    step: '03',
    color: '#ff5b4f',
  },
  {
    title: 'Grant Assistant',
    description: 'Reframe your protocol for NIH R01, Wellcome, or EU Horizon grant formats in one click.',
    step: '04',
    color: '#0a72ef',
  },
  {
    title: 'Similarity Analysis',
    description: 'Visualize how your papers relate to each other with an AI-scored similarity matrix.',
    step: '05',
    color: '#de1d8d',
  },
  {
    title: 'Literature Review',
    description: 'Synthesise your workspace into a structured academic literature review with six sections.',
    step: '06',
    color: '#ff5b4f',
  },
];

const steps = [
  {
    label: 'Collect',
    color: '#0a72ef',
    title: 'Search & Build Workspace',
    description: 'Search across 5 academic databases at once and add the papers that matter to your workspace.',
  },
  {
    label: 'Analyse',
    color: '#de1d8d',
    title: 'Extract Insights',
    description: 'Run AI similarity analysis, extract methods, and generate testable hypotheses from your collection.',
  },
  {
    label: 'Create',
    color: '#ff5b4f',
    title: 'Generate & Export',
    description: 'Build a full research protocol, reframe for a grant application, and export to PDF.',
  },
];

const FREE_FEATURES = [
  '5 searches per day',
  '3 free protocol generations',
  'Workspace management',
  'Zotero sync',
  'Reference formatter',
  'Save up to 3 workspaces',
];

const PRO_FEATURES = [
  'Everything in Free',
  'Unlimited searches',
  '50 AI credits/month (~25 protocol generations)',
  'Full 6-section protocol generation with citations',
  'Hypothesis generator with experimental rationale',
  'Grant assistant (NIH R01, Wellcome, EU Horizon)',
  'AI literature review draft',
  'Similarity analysis matrix',
  'PDF & DOCX export',
  'Zotero sync + protocol version history',
  'Priority email support',
];

const FAQ_ITEMS = [
  {
    q: "I'm a PhD student — can Protovera help me write my first experimental protocol?",
    a: "Yes. Search the databases for your method, add 3–10 relevant papers to your workspace, and generate a fully cited 6-section protocol in minutes. The free plan covers 5 searches per day and 3 lifetime protocol generations — no credit card required. Upgrade to Pro when you need unlimited generations for your thesis chapters.",
  },
  {
    q: "I'm a PI managing multiple projects — how does Protovera save my lab time?",
    a: "Protovera lets you build separate workspaces per project and generate literature-grounded protocols in minutes instead of days. The Lab plan ($49/month) gives 5 seats with a shared credit pool, a shared protocol library, and a lab manager dashboard — so you can track what every team member has generated.",
  },
  {
    q: "Can biotech or pharma researchers use Protovera for experimental documentation?",
    a: "Yes. Protovera generates structured experimental protocols with inline citations from peer-reviewed literature, which you can export to DOCX for further editing within your organisation's documentation workflow. Final protocol validation and GMP/GLP compliance review remain with your team.",
  },
  {
    q: "Is Protovera suitable for CRO scientists who write SOPs and study protocols?",
    a: "Yes. CRO scientists use Protovera to accelerate the literature search and first-draft phases of protocol writing. Search PubMed, arXiv, OpenAlex, Europe PMC, and bioRxiv simultaneously, select the most relevant methods papers, and generate a structured draft with inline citations — then adapt it to your client's SOP format in DOCX.",
  },
  {
    q: "Can Protovera help with NIH, Wellcome Trust, or EU Horizon grant applications?",
    a: "Yes. The Grant Assistant reformats your literature-grounded protocol into grant-ready sections tailored to NIH R01, Wellcome Trust, or EU Horizon formats. It restructures the content to match each funder's required headings while preserving the scientific rigour and citations from your original protocol.",
  },
  {
    q: "How long does it take to generate a research protocol?",
    a: "Most protocols are generated in under 2 minutes. Searching databases takes a few seconds. Building your workspace — reading abstracts, selecting papers — typically takes 10–20 minutes for a new topic. The AI generation step itself runs in 60–90 seconds.",
  },
  {
    q: "What file formats can I export my protocol in?",
    a: "Pro and Lab plan users can export to PDF (formatted, print-ready) or DOCX (editable Word document). The DOCX export includes all section headings, inline citations, and paragraph structure, ready to paste into your institution's protocol template.",
  },
  {
    q: "Is my research data kept private?",
    a: "Yes. Your workspace papers, generated protocols, and search queries are private to your account. We do not use your research content to train AI models. You can delete your account and all associated data at any time by contacting info@protovera.org.",
  },
];

function CheckIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

function FaqAccordion() {
  const [openIdx, setOpenIdx] = useState(null);
  return (
    <div className="divide-y divide-[#ebebeb]" style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px', borderRadius: '12px', overflow: 'hidden' }}>
      {FAQ_ITEMS.map((item, i) => (
        <div key={i} className="bg-white">
          <button
            onClick={() => setOpenIdx(openIdx === i ? null : i)}
            className="w-full flex items-center justify-between px-6 py-5 text-left gap-4 hover:bg-[#fafafa] transition-colors"
          >
            <span className="text-sm font-medium text-[#171717]" style={{ letterSpacing: '-0.16px' }}>{item.q}</span>
            <span className="flex-shrink-0 text-[#808080] text-lg leading-none">{openIdx === i ? '−' : '+'}</span>
          </button>
          {openIdx === i && (
            <div className="px-6 pb-5">
              <p className="text-sm text-[#4d4d4d] leading-relaxed">{item.a}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function Landing() {
  const { user } = useAuth();

  return (
    <div className="bg-white min-h-screen font-sans">
      {/* Navigation */}
      <header
        className="sticky top-0 z-10 bg-white/95 backdrop-blur-sm"
        style={{ boxShadow: 'rgba(0,0,0,0.08) 0px -1px 0px 0px inset' }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3.5 flex items-center justify-between">
          <Link to="/research" className="text-sm font-semibold text-[#171717]" style={{ letterSpacing: '-0.02rem' }}>
            Protovera
          </Link>
          <nav className="flex items-center gap-1">
            <Link
              to="/pricing"
              className="px-3 py-1.5 text-sm font-medium text-[#4d4d4d] hover:text-[#171717] transition-colors rounded-md"
            >
              Pricing
            </Link>
            {user ? (
              <Link
                to="/app"
                className="ml-1 px-4 py-1.5 bg-[#171717] text-white text-sm font-medium rounded-md hover:bg-[#2e2e2e] transition-colors"
              >
                Dashboard
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  className="px-3 py-1.5 text-sm font-medium text-[#4d4d4d] hover:text-[#171717] transition-colors rounded-md"
                >
                  Sign In
                </Link>
                <Link
                  to="/register"
                  className="ml-1 px-4 py-1.5 bg-[#171717] text-white text-sm font-medium rounded-md hover:bg-[#2e2e2e] transition-colors"
                >
                  Get Started
                </Link>
              </>
            )}
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-24 pb-20 sm:pt-32 sm:pb-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div
            className="inline-flex items-center gap-2 mb-8 px-3 py-1 rounded-full text-xs font-medium text-[#4d4d4d]"
            style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#0a72ef] flex-shrink-0" />
            For Life Sciences Researchers
          </div>

          <h1
            className="text-5xl sm:text-6xl font-semibold text-[#171717] leading-[1.05] mb-6"
            style={{ letterSpacing: '-2.88px' }}
          >
            Protovera turns literature<br className="hidden sm:block" /> into grounded research plans
          </h1>

          <p className="text-lg sm:text-xl text-[#4d4d4d] max-w-2xl mx-auto mb-10 leading-relaxed">
            Search across PubMed, bioRxiv, OpenAlex, and more. Protovera turns your curated papers
            into research protocols, hypotheses, and grant-ready sections grounded in <em>your</em> literature,
            not generic model output.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-6">
            <Link
              to="/register"
              className="w-full sm:w-auto px-8 py-3 bg-[#171717] text-white font-medium rounded-md hover:bg-[#2e2e2e] transition-colors text-sm"
            >
              Get Started Free
            </Link>
            <Link
              to="/pricing"
              className="w-full sm:w-auto px-8 py-3 text-[#4d4d4d] font-medium rounded-md text-sm transition-all hover:text-[#171717]"
              style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
            >
              See Pricing
            </Link>
          </div>

          <p className="text-sm text-[#808080]">
            No credit card required · Free plan available
          </p>
        </div>
      </section>

      {/* Product Preview */}
      <section className="pb-24 overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <p className="text-xs font-semibold uppercase tracking-widest text-[#808080] mb-3">Product Preview</p>
            <h2 className="text-3xl font-semibold text-[#171717]" style={{ letterSpacing: '-1.6px' }}>
              From papers to protocol in minutes
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            {/* Protocol output mockup — wider */}
            <div
              className="lg:col-span-3 rounded-xl overflow-hidden"
              style={{ boxShadow: 'rgba(0,0,0,0.10) 0px 0px 0px 1px, rgba(0,0,0,0.06) 0px 4px 12px' }}
            >
              {/* Browser chrome */}
              <div className="bg-[#f5f5f5] px-4 py-2.5 flex items-center gap-2 border-b border-[#e5e5e5]">
                <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
                <span className="ml-3 text-xs text-[#808080] bg-white rounded px-3 py-0.5 flex-1 max-w-xs" style={{ boxShadow: 'rgba(0,0,0,0.06) 0px 0px 0px 1px' }}>
                  protovera.org/app
                </span>
              </div>
              {/* Content */}
              <div className="bg-white p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-[#808080] mb-0.5">Research Protocol</p>
                    <h3 className="text-sm font-semibold text-[#171717]" style={{ letterSpacing: '-0.32px' }}>
                      CRISPR-Cas9 Knockout in HEK293T Cells
                    </h3>
                  </div>
                  <span className="text-[10px] font-medium px-2.5 py-1 rounded text-[#4d4d4d]" style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}>
                    Export PDF
                  </span>
                </div>

                {/* Section: Background */}
                <div className="mb-4 pb-4 border-b border-[#f0f0f0]">
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-[#0a72ef] mb-2">Background &amp; Rationale</p>
                  <p className="text-xs text-[#4d4d4d] leading-relaxed">
                    CRISPR-Cas9 enables precise genomic edits via RNA-guided nucleases
                    {' '}<span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium text-[#de1d8d]" style={{ background: 'rgba(222,29,141,0.07)' }}>[Jinek et al., 2012]</span>
                    {' '}achieving editing efficiencies &gt;80% in HEK293T cells
                    {' '}<span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium text-[#de1d8d]" style={{ background: 'rgba(222,29,141,0.07)' }}>[Ran et al., 2013]</span>.
                    {' '}Off-target activity at paralogous loci remains a key concern
                    {' '}<span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium text-[#de1d8d]" style={{ background: 'rgba(222,29,141,0.07)' }}>[Cong et al., 2013]</span>.
                  </p>
                </div>

                {/* Section: Methods */}
                <div className="mb-4 pb-4 border-b border-[#f0f0f0]">
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-[#0a72ef] mb-2">Step-by-step Methods</p>
                  <ol className="space-y-1.5">
                    {[
                      { text: 'Seed 2×10⁵ HEK293T cells per well of a 24-well plate 24 h prior to transfection.', cite: 'Ran et al., 2013' },
                      { text: 'Prepare RNP complex: Cas9 (1 µg) + sgRNA (0.5 µg) at 37 °C for 10 min.', cite: 'Ran et al., 2013' },
                      { text: 'Mix with Lipofectamine 3000 per manufacturer protocol; incubate 48–72 h.', cite: null },
                      { text: 'Extract genomic DNA and PCR-amplify a 500–700 bp product spanning the cut site.', cite: 'Jinek et al., 2012' },
                    ].map((step, i) => (
                      <li key={i} className="flex gap-2 text-xs text-[#4d4d4d]">
                        <span className="flex-shrink-0 w-4 h-4 rounded-full bg-[#f5f5f5] flex items-center justify-center text-[9px] font-semibold text-[#808080]">{i + 1}</span>
                        <span className="leading-relaxed">
                          {step.text}
                          {step.cite && (
                            <>{' '}<span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium text-[#de1d8d]" style={{ background: 'rgba(222,29,141,0.07)' }}>[{step.cite}]</span></>
                          )}
                        </span>
                      </li>
                    ))}
                  </ol>
                </div>

                {/* Section: References stub */}
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-[#0a72ef] mb-2">References</p>
                  <div className="space-y-1">
                    {['Jinek M et al. (2012). Science.', 'Ran FA et al. (2013). Nature Protocols.', 'Cong L et al. (2013). Science.'].map((ref) => (
                      <p key={ref} className="text-[10px] text-[#808080] leading-snug">{ref}</p>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Knowledge graph mockup — narrower */}
            <div
              className="lg:col-span-2 rounded-xl overflow-hidden"
              style={{ boxShadow: 'rgba(0,0,0,0.10) 0px 0px 0px 1px, rgba(0,0,0,0.06) 0px 4px 12px' }}
            >
              <div className="bg-[#f5f5f5] px-4 py-2.5 flex items-center gap-2 border-b border-[#e5e5e5]">
                <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
                <span className="ml-3 text-xs text-[#808080] bg-white rounded px-3 py-0.5 flex-1 max-w-xs" style={{ boxShadow: 'rgba(0,0,0,0.06) 0px 0px 0px 1px' }}>
                  Knowledge Graph
                </span>
              </div>
              <div className="bg-[#fafafa] p-4 flex flex-col h-full" style={{ minHeight: '360px' }}>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-semibold text-[#171717]" style={{ letterSpacing: '-0.24px' }}>Concept Map · 4 papers</p>
                  <span className="text-[10px] text-[#808080]">Live connections</span>
                </div>
                <div className="flex-1 relative">
                  <svg viewBox="0 0 280 300" className="w-full h-full" style={{ maxHeight: '320px' }}>
                    {/* Edges */}
                    <line x1="140" y1="140" x2="60" y2="55" stroke="#e5e5e5" strokeWidth="1.5" />
                    <line x1="140" y1="140" x2="220" y2="55" stroke="#e5e5e5" strokeWidth="1.5" />
                    <line x1="140" y1="140" x2="55" y2="230" stroke="#e5e5e5" strokeWidth="1.5" />
                    <line x1="140" y1="140" x2="225" y2="230" stroke="#e5e5e5" strokeWidth="1.5" />
                    <line x1="140" y1="140" x2="140" y2="38" stroke="#e5e5e5" strokeWidth="1.5" />
                    {/* Concept-to-concept faint connections */}
                    <line x1="60" y1="55" x2="220" y2="55" stroke="#f0f0f0" strokeWidth="1" strokeDasharray="3,3" />
                    <line x1="55" y1="230" x2="225" y2="230" stroke="#f0f0f0" strokeWidth="1" strokeDasharray="3,3" />
                    <line x1="60" y1="55" x2="55" y2="230" stroke="#f0f0f0" strokeWidth="1" strokeDasharray="3,3" />

                    {/* Centre concept node */}
                    <circle cx="140" cy="140" r="20" fill="#0a72ef" opacity="0.12" />
                    <circle cx="140" cy="140" r="14" fill="#0a72ef" opacity="0.20" />
                    <circle cx="140" cy="140" r="8" fill="#0a72ef" />
                    <text x="140" y="166" textAnchor="middle" fontSize="8.5" fill="#171717" fontWeight="600">CRISPR-Cas9</text>

                    {/* Paper node: top-left */}
                    <circle cx="60" cy="55" r="16" fill="white" stroke="#e5e5e5" strokeWidth="1.5" />
                    <text x="60" y="52" textAnchor="middle" fontSize="7" fill="#4d4d4d" fontWeight="600">P1</text>
                    <text x="60" y="61" textAnchor="middle" fontSize="6.5" fill="#808080">Jinek</text>
                    <text x="60" y="70" textAnchor="middle" fontSize="6" fill="#808080">2012</text>

                    {/* Paper node: top-right */}
                    <circle cx="220" cy="55" r="16" fill="white" stroke="#e5e5e5" strokeWidth="1.5" />
                    <text x="220" y="52" textAnchor="middle" fontSize="7" fill="#4d4d4d" fontWeight="600">P2</text>
                    <text x="220" y="61" textAnchor="middle" fontSize="6.5" fill="#808080">Ran</text>
                    <text x="220" y="70" textAnchor="middle" fontSize="6" fill="#808080">2013</text>

                    {/* Paper node: bottom-left */}
                    <circle cx="55" cy="230" r="16" fill="white" stroke="#e5e5e5" strokeWidth="1.5" />
                    <text x="55" y="227" textAnchor="middle" fontSize="7" fill="#4d4d4d" fontWeight="600">P3</text>
                    <text x="55" y="236" textAnchor="middle" fontSize="6.5" fill="#808080">Cong</text>
                    <text x="55" y="245" textAnchor="middle" fontSize="6" fill="#808080">2013</text>

                    {/* Paper node: bottom-right */}
                    <circle cx="225" cy="230" r="16" fill="white" stroke="#e5e5e5" strokeWidth="1.5" />
                    <text x="225" y="227" textAnchor="middle" fontSize="7" fill="#4d4d4d" fontWeight="600">P4</text>
                    <text x="225" y="236" textAnchor="middle" fontSize="6.5" fill="#808080">Mali</text>
                    <text x="225" y="245" textAnchor="middle" fontSize="6" fill="#808080">2013</text>

                    {/* Paper node: top-center */}
                    <circle cx="140" cy="38" r="13" fill="white" stroke="#de1d8d" strokeWidth="1.5" strokeDasharray="2,2" />
                    <text x="140" y="35" textAnchor="middle" fontSize="7" fill="#de1d8d" fontWeight="600">P5</text>
                    <text x="140" y="44" textAnchor="middle" fontSize="6" fill="#de1d8d">+workspace</text>

                    {/* Concept badges */}
                    <rect x="96" y="188" width="52" height="14" rx="7" fill="rgba(10,114,239,0.08)" />
                    <text x="122" y="198" textAnchor="middle" fontSize="7" fill="#0a72ef" fontWeight="600">Gene Editing</text>

                    <rect x="150" y="105" width="50" height="14" rx="7" fill="rgba(255,91,79,0.08)" />
                    <text x="175" y="115" textAnchor="middle" fontSize="7" fill="#ff5b4f" fontWeight="600">sgRNA Design</text>

                    <rect x="56" y="132" width="46" height="14" rx="7" fill="rgba(222,29,141,0.08)" />
                    <text x="79" y="142" textAnchor="middle" fontSize="7" fill="#de1d8d" fontWeight="600">HEK293T</text>
                  </svg>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {['Gene editing', 'sgRNA design', 'Off-target effects', 'HDR pathway'].map((tag) => (
                    <span
                      key={tag}
                      className="text-[10px] font-medium px-2 py-0.5 rounded-full text-[#4d4d4d]"
                      style={{ background: 'rgba(0,0,0,0.04)', boxShadow: 'rgba(0,0,0,0.06) 0px 0px 0px 1px' }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <p className="text-center text-xs text-[#808080] mt-5">
            All claims in the protocol trace back to your uploaded papers — no hallucination.
          </p>
        </div>
      </section>

      {/* Stats bar */}
      <section className="border-y border-[#ebebeb]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 text-center">
            {[
              { value: '5', label: 'Academic Databases' },
              { value: '7', label: 'AI Writing Tools' },
              { value: 'PDF+DOCX', label: 'Export Formats' },
              { value: '$0', label: 'to Start' },
            ].map((stat) => (
              <div key={stat.label}>
                <div
                  className="text-3xl font-semibold text-[#171717] mb-1.5"
                  style={{ letterSpacing: '-1.28px' }}
                >
                  {stat.value}
                </div>
                <div className="text-sm text-[#808080]">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <h2
              className="text-4xl font-semibold text-[#171717] mb-4"
              style={{ letterSpacing: '-2.4px' }}
            >
              Everything in one workflow
            </h2>
            <p className="text-lg text-[#4d4d4d] max-w-xl mx-auto leading-relaxed">
              Protovera keeps literature search, protocol drafting, and research writing in one workflow.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {features.map((feature) => (
              <div
                key={feature.title}
                className="bg-white rounded-lg p-6"
                style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px, rgba(0,0,0,0.04) 0px 2px 2px, #fafafa 0px 0px 0px 1px inset' }}
              >
                <div
                  className="text-xs font-semibold uppercase tracking-widest mb-4"
                  style={{ color: feature.color }}
                >
                  {feature.step}
                </div>
                <h3
                  className="text-base font-semibold text-[#171717] mb-2"
                  style={{ letterSpacing: '-0.32px' }}
                >
                  {feature.title}
                </h3>
                <p className="text-sm text-[#4d4d4d] leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-24 border-y border-[#ebebeb]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <h2
              className="text-4xl font-semibold text-[#171717] mb-4"
              style={{ letterSpacing: '-2.4px' }}
            >
              How it works
            </h2>
            <p className="text-lg text-[#4d4d4d] max-w-lg mx-auto">
              Three steps from your first search to an exportable protocol built from your sources.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-12">
            {steps.map((step, index) => (
              <div key={step.label} className="relative">
                {index < steps.length - 1 && (
                  <div
                    className="hidden sm:block absolute top-3 left-[calc(100%+1rem)] right-[-1rem] border-t border-dashed border-[#ebebeb]"
                    style={{ width: 'calc(100% - 2rem)' }}
                  />
                )}
                <div
                  className="text-xs font-semibold uppercase tracking-widest mb-4"
                  style={{ color: step.color }}
                >
                  {step.label}
                </div>
                <h3
                  className="text-base font-semibold text-[#171717] mb-2"
                  style={{ letterSpacing: '-0.32px' }}
                >
                  {step.title}
                </h3>
                <p className="text-sm text-[#4d4d4d] leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2
              className="text-4xl font-semibold text-[#171717] mb-4"
              style={{ letterSpacing: '-2.4px' }}
            >
              Simple pricing
            </h2>
          <p className="text-lg text-[#4d4d4d]">Start with search and upgrade when you need full AI drafting.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Free */}
            <div
              className="rounded-lg p-8 flex flex-col"
              style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px, rgba(0,0,0,0.04) 0px 2px 2px, #fafafa 0px 0px 0px 1px inset' }}
            >
              <div className="mb-6">
                <div className="text-xs font-semibold uppercase tracking-widest text-[#808080] mb-3">Free</div>
                <div
                  className="text-4xl font-semibold text-[#171717] mb-1"
                  style={{ letterSpacing: '-1.28px' }}
                >
                  $0
                </div>
                <p className="text-sm text-[#808080]">Forever free</p>
              </div>
              <ul className="space-y-3 mb-8 flex-1">
                {FREE_FEATURES.map((item) => (
                  <li key={item} className="flex items-center gap-2.5 text-sm text-[#4d4d4d]">
                    <CheckIcon className="w-3.5 h-3.5 text-[#171717] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link
                to="/register"
                className="block text-center px-6 py-2.5 text-sm font-medium text-[#171717] rounded-md transition-all hover:bg-[#fafafa]"
                style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
              >
                Start Free
              </Link>
            </div>

            {/* Pro */}
            <div className="rounded-lg p-8 flex flex-col bg-[#171717] relative">
              <div className="absolute top-5 right-5 px-2.5 py-0.5 bg-white/10 text-white/80 text-xs font-medium rounded-full">
                Most Popular
              </div>
              <div className="mb-6">
                <div className="text-xs font-semibold uppercase tracking-widest text-white/40 mb-3">Pro</div>
                <div
                  className="text-4xl font-semibold text-white mb-1"
                  style={{ letterSpacing: '-1.28px' }}
                >
                  $15<span className="text-base font-normal text-white/40">/mo</span>
                </div>
                <p className="text-sm text-white/40">Billed monthly</p>
              </div>
              <ul className="space-y-3 mb-8 flex-1">
                {PRO_FEATURES.map((item) => (
                  <li key={item} className="flex items-center gap-2.5 text-sm text-white/70">
                    <CheckIcon className="w-3.5 h-3.5 text-white/60 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link
                to="/pricing"
                className="block text-center px-6 py-2.5 bg-white text-[#171717] text-sm font-medium rounded-md hover:bg-white/90 transition-colors"
              >
                Upgrade to Pro
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 border-t border-[#ebebeb]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-semibold text-[#171717] mb-4" style={{ letterSpacing: '-2.4px' }}>
              Frequently asked questions
            </h2>
            <p className="text-lg text-[#4d4d4d]">For PhD students, PIs, biotech, and CRO researchers.</p>
          </div>
          <FaqAccordion />
        </div>
      </section>

      {/* Footer CTA */}
      <section className="border-t border-[#ebebeb] py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <h2
            className="text-4xl font-semibold text-[#171717] mb-4"
            style={{ letterSpacing: '-2.4px' }}
          >
            Start with Protovera
          </h2>
          <p className="text-lg text-[#4d4d4d] mb-8 leading-relaxed">
            Build literature-backed protocols, hypotheses, and reviews faster without losing source grounding.
          </p>
          <Link
            to="/register"
            className="inline-block px-8 py-3 bg-[#171717] text-white font-medium rounded-md hover:bg-[#2e2e2e] transition-colors text-sm"
          >
            Create Free Account
          </Link>
          <p className="text-[#808080] text-sm mt-4">No credit card required</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#ebebeb] py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-[#808080]">
          <span className="font-medium text-[#171717]">Protovera</span>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1">
            <Link to="/pricing" className="hover:text-[#171717] transition-colors">Pricing</Link>
            <Link to="/login" className="hover:text-[#171717] transition-colors">Sign In</Link>
            <Link to="/register" className="hover:text-[#171717] transition-colors">Register</Link>
            <Link to="/privacy" className="hover:text-[#171717] transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-[#171717] transition-colors">Terms</Link>
          </div>
          <span>PubMed · arXiv · OpenAlex · and more</span>
        </div>
      </footer>
    </div>
  );
}
